import { type RefObject, useRef } from "react";
import { useCallback, useLayoutEffect, useState } from "react";

import { useResizeObserver } from "app/custom-hooks/useResizeObserver";

/**
 * Returns the correct `scroll.y` value for an ant-design virtual table.
 *
 * `scroll.y` controls the height of the **table body only** (the virtual viewport).
 * Header and pagination render outside it, so we must subtract their heights from
 * the container to prevent overflow/clipping.
 */
export function useVirtualTableScrollY(
    containerRef: RefObject<HTMLElement | null>,
    /** When true, freezes the current scrollY value (e.g. during row expansion) */
    frozen = false,
): number {
    const [scrollY, setScrollY] = useState(0);

    const frozenRef = useRef(frozen);
    frozenRef.current = frozen;

    const measure = useCallback(() => {
        const el = containerRef.current;
        if (!el || frozenRef.current) {
            return;
        }

        const containerH = el.clientHeight;
        const headerH = el.querySelector<HTMLElement>(".ant-table-header")?.offsetHeight ?? 0;
        const paginationH = el.querySelector<HTMLElement>(".ant-table-pagination")?.offsetHeight ?? 0;

        setScrollY(Math.max(0, containerH - headerH - paginationH));
    }, [containerRef, frozenRef]);

    useResizeObserver({ onResize: measure, observingElement: containerRef.current });

    useLayoutEffect(() => {
        // measearing on each re-render of the table, so that the virtual table can pick up height changes.
        measure();
    });

    return scrollY;
}
